import { apiFetch } from '@/lib/api-fetch.js';
import type {
  GeneratedCasePublicationBatch,
  ProposalCase,
  Run,
  CoverageSummary,
  RequirementInventoryEntry,
} from './quality-types.js';

export type GenerationSourceReceipt = {
  sourceId: string;
  group: string;
  label: string;
  scope: string;
  collection: string;
  consumption: string;
  reason?: string;
  receivedItems: number | null;
  knownTotal: number | null;
  snapshotSha256: string | null;
  forwardedFragments: number;
  omittedFragments: number;
  anchors: string[];
};
export type GenerationCaseBasis = {
  field: string;
  role: string;
  evidenceIds: string[];
  explanation: string;
};
export type GenerationBasketSelection = {
  selectedIndices: number[];
  choices: {
    caseIndex: number;
    variables: { key: string; reuseExisting?: boolean }[];
    disabledExistingKeys?: string[];
  }[];
};
export type GenerationPreviewVariable = {
  key: string;
  effectiveKey: string;
  defaultValue: string;
  meaning: string;
  rationale: string;
  caseIndices: number[];
  enabledCaseIndices: number[];
  state: 'new' | 'reuse_candidate' | 'blocked';
  reason?: string;
};
export type GenerationBasketPreview = {
  schemaVersion: 1;
  version: number;
  digest: string;
  selection: GenerationBasketSelection;
  cases: { caseIndex: number; literal: ProposalCase; rendered: ProposalCase }[];
  variables: GenerationPreviewVariable[];
  existingVariables?: {
    key: string;
    value: string | null;
    scope: string;
    caseIndices: number[];
    enabledCaseIndices: number[];
    available: boolean;
    reason?: string;
  }[];
  executionContextSha256?: string;
  createVariables: Record<string, string>;
  warnings: string[];
  coverage?: {
    summary: CoverageSummary;
    inventory: RequirementInventoryEntry[];
  };
};
export type GenerationEvidencePage = {
  schemaVersion: 1;
  version: number;
  state: string;
  sources: GenerationSourceReceipt[];
  receipt: {
    available: boolean;
    complete: boolean;
    entryCount: number;
    omittedCount: number;
    truncatedCount: number;
    reason?: string;
  };
  entries: {
    anchorId: string;
    tool: string;
    role: string;
    delivery: string;
    actionInput: string;
    inputTruncated: boolean;
    result: string;
    resultTruncated: boolean;
    source: { kind: string; label: string };
  }[];
  nextOffset: number | null;
  previousOffset?: number | null;
  initialPrompt: { text: string; sha256: string; truncated: boolean } | null;
  initialPromptUnavailableReason: string | null;
};

async function generationRequest<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await apiFetch(`/v1/quality${path}`, init);
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;
    throw new Error(
      typeof data?.message === 'string'
        ? data.message
        : `Generation request failed (${response.status})`,
    );
  }
  return (await response.json()) as T;
}
function proposalPath(runId: string, proposalId: string) {
  return `/runs/${encodeURIComponent(runId)}/proposals/${encodeURIComponent(proposalId)}`;
}
/** Existing generation REST DTOs use camelCase; keep this exact validated contract. */
export function previewGenerationBasket(
  runId: string,
  proposalId: string,
  version: number,
  selection: GenerationBasketSelection,
) {
  return generationRequest<GenerationBasketPreview>(
    `${proposalPath(runId, proposalId)}/basket-preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version, selection }),
    },
  );
}
export function fetchGenerationEvidence(
  runId: string,
  proposalId: string,
  offset = 0,
  sourceId?: string,
  anchorIds?: string[],
) {
  const query = new URLSearchParams({ offset: String(offset) });
  if (sourceId) query.set('source_id', sourceId);
  if (anchorIds) query.set('anchor_ids', anchorIds.join(','));
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(
        new Error(
          'Source data could not be loaded within 20 seconds. Try again.',
        ),
      );
    }, 20_000);
  });
  return Promise.race([
    generationRequest<GenerationEvidencePage>(
      `${proposalPath(runId, proposalId)}/generation-evidence?${query}`,
      { signal: controller.signal },
    ),
    deadline,
  ]).finally(() => clearTimeout(timer));
}
export function resumeGenerationPublication(
  runId: string,
  proposalId: string,
  batchId: string,
) {
  return generationRequest<{
    batch: GeneratedCasePublicationBatch;
    run: Run;
    skipped_existing: unknown[];
  }>(
    `${proposalPath(runId, proposalId)}/publication-batches/${encodeURIComponent(batchId)}/resume`,
    { method: 'POST' },
  );
}
